import { useCallback, useMemo } from "react";
import { toast } from "react-toastify";

import { EventActionTypeEnum } from "~/feature/event/components/ActionButtons/types";
import { useWatchlist } from "~/feature/watchlist/providers/WatchlistProvider";
import type { IAggregatedEvent } from "~/types/event";

import {
  iconByActionType,
  iconColorByActionType,
  labelByActionType,
} from "./constants";
import { copyTextToClipboard, shareUrl } from "./utils";

export const useActionButtons = (event: IAggregatedEvent) => {
  const { toggleWatchlist, isEventAlreadyInWatchlist } = useWatchlist();

  const isInWatchlist = useMemo(
    () => isEventAlreadyInWatchlist(event.groupId || ""),
    [event, isEventAlreadyInWatchlist]
  );

  const handleToggleWatchlist = useCallback(
    () => void toggleWatchlist(event.groupId || ""),
    [event.groupId, toggleWatchlist]
  );

  const copyCurrentUrl = useCallback(async () => {
    const ok = await copyTextToClipboard(window.location.href);
    if (ok) toast.success("Link copied");
    else toast.error("Failed to copy link");
  }, []);

  const share = useCallback(async () => {
    const result = await shareUrl({
      title: event.title,
      url: window.location.href,
    });
    if (result === "copied") toast.success("Link copied");
    else if (result === "failed") toast.error("Failed to share");
  }, [event.title]);

  const itemsMap = useMemo(
    () => ({
      [EventActionTypeEnum.AddToWatchlist]: {
        Icon: iconByActionType[EventActionTypeEnum.AddToWatchlist],
        label: labelByActionType[EventActionTypeEnum.AddToWatchlist],
        onClick: handleToggleWatchlist,
        hidden: isInWatchlist,
        color: iconColorByActionType[EventActionTypeEnum.AddToWatchlist],
      },
      [EventActionTypeEnum.RemoveFromWatchlist]: {
        Icon: iconByActionType[EventActionTypeEnum.RemoveFromWatchlist],
        label: labelByActionType[EventActionTypeEnum.RemoveFromWatchlist],
        onClick: handleToggleWatchlist,
        hidden: !isInWatchlist,
        color: iconColorByActionType[EventActionTypeEnum.RemoveFromWatchlist],
      },
      [EventActionTypeEnum.CopyLink]: {
        Icon: iconByActionType[EventActionTypeEnum.CopyLink],
        label: labelByActionType[EventActionTypeEnum.CopyLink],
        onClick: copyCurrentUrl,
        hidden: false,
        color: iconColorByActionType[EventActionTypeEnum.CopyLink],
      },
      [EventActionTypeEnum.Share]: {
        Icon: iconByActionType[EventActionTypeEnum.Share],
        label: labelByActionType[EventActionTypeEnum.Share],
        onClick: share,
        hidden: false,
        color: iconColorByActionType[EventActionTypeEnum.Share],
      },
    }),
    [copyCurrentUrl, handleToggleWatchlist, isInWatchlist, share]
  );

  return {
    actionItems: Object.values(EventActionTypeEnum)
      .map((key) => itemsMap[key])
      .filter((item) => !item.hidden),
  };
};
